import {
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import type { Accent } from '../configs';
import { ACCENTS } from '../configs';
import type { ThemeModeValue } from './type';
import { ThemeMode } from './type';

const systemThemeModeMatch = window.matchMedia('(prefers-color-scheme: dark)');

const initialThemeMode = getLocalThemeMode();
const initialAccent = getLocalAccent();

export type ThemeContextValue = ReturnType<typeof useThemeContextValue>;

export const themeContext = createContext<ThemeContextValue>({} as never);

export function useThemeContextValue() {
  const [themeMode, setThemeMode] = useState<ThemeMode>(initialThemeMode);
  const [accent, setAccent] = useState<Accent>(initialAccent);
  const [scale, setScale] = useState<number>(0);
  const themeRootRef = useRef<HTMLDivElement>(null);

  const systemThemeMode = useSyncExternalStore<ThemeModeValue>(
    (cb) => {
      systemThemeModeMatch.addEventListener('change', cb);
      return () => {
        systemThemeModeMatch.removeEventListener('change', cb);
      };
    },
    () => {
      return systemThemeModeMatch.matches ? ThemeMode.DARK : ThemeMode.LIGHT;
    }
  );

  const themeModeValue = useMemo<ThemeModeValue>(() => {
    switch (themeMode) {
      case ThemeMode.AUTO:
        return systemThemeMode;
      default:
        return themeMode;
    }
  }, [themeMode, systemThemeMode]);

  const value = useMemo(
    () => ({
      themeMode,
      setThemeMode,
      themeModeValue,
      accent,
      setAccent,
      scale,
      setScale,
      themeRootRef,
    }),
    [themeMode, themeModeValue, accent, scale]
  );

  useLayoutEffect(() => {
    setLocalThemeMode(themeMode);
    setLocalAccent(accent);
  }, [themeMode, accent]);

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.dataset.themeModeValue = themeModeValue;
    root.classList.toggle(ThemeMode.DARK, themeModeValue === ThemeMode.DARK);
    root.classList.toggle(ThemeMode.LIGHT, themeModeValue === ThemeMode.LIGHT);
  }, [themeModeValue]);

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--text-scale-factor', `${scale}px`);
  }, [scale]);

  return value;
}

export function useThemeContext() {
  return useContext(themeContext);
}

export function getLocalThemeMode() {
  return (localStorage.getItem('theme-mode') as ThemeMode) ?? ThemeMode.AUTO;
}

export function getLocalAccent() {
  return (localStorage.getItem('accent') as Accent) ?? ACCENTS[0];
}

export function setLocalThemeMode(mode: ThemeMode) {
  return localStorage.setItem('theme-mode', mode);
}

export function setLocalAccent(accent: Accent) {
  return localStorage.setItem('accent', accent);
}
